import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/composites/PageHeader';
import { DataTable } from '@/components/composites/DataTable';
import { Button } from '@/components/primitives/Button';
import { Select } from '@/components/primitives/Select';
import { useAppContext } from '@/context/AppContext';
import { useActivityLog } from '@/hooks/useActivityLog';
import { useClients } from '@/hooks/useClients';
import { useProjects } from '@/hooks/useProjects';
import { useEmployees } from '@/hooks/useEmployees';
import { formatDayShort } from '@/lib/dates';
import type { ActivityLogGroupBy, ActivityLogRow } from '@/types/reports';
import styles from './ActivityLogPage.module.css';

const FILTER_ALL = '';

type ColumnKey = 'group' | 'titleApp' | 'sessions' | 'totalHours' | 'dateRange';

interface GroupByChoice {
  id: 'none' | ActivityLogGroupBy;
  label: string;
  /** URL value: undefined means the param is omitted. */
  value: ActivityLogGroupBy | undefined;
}
const GROUP_BY_CHOICES: readonly GroupByChoice[] = [
  { id: 'none', label: 'None', value: undefined },
  { id: 'client', label: 'Client', value: 'client' },
  { id: 'project', label: 'Project', value: 'project' },
] as const;

/**
 * Activity log report page — task 169.
 *
 * Flat list of (app, title) rows for an org's classified events, aggregated
 * across employees, scoped by the same filter set as /reports/grouped. The
 * row's `eventCount` is the screenshot count (the system stores one
 * screenshot per event); a future drill-down to the screenshot stream will
 * hang off this surface.
 *
 * Optional `groupBy=client|project` adds a Client or Project column at the
 * front of the table. Rows are split per group — the same `(app, title)`
 * under two projects yields two rows.
 */
export function ActivityLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mockupMode, dateRange } = useAppContext();

  // Period comes from the global top-bar DateRangePicker via AppContext —
  // mirrors the Grouped, Applications, and Domain Analytics pages. The page
  // does not host its own period selector. The activity-log backend route
  // accepts the monthly report's full period grammar; we always emit
  // `period=custom` because the global picker stores a `{ start, end }`
  // literal.
  const periodQs = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return null;
    const params = new URLSearchParams();
    params.set('period', 'custom');
    params.set('startDate', dateRange.start);
    params.set('endDate', dateRange.end);
    return params.toString();
  }, [dateRange.start, dateRange.end]);

  const clientId = searchParams.get('clientId') ?? FILTER_ALL;
  const projectId = searchParams.get('projectId') ?? FILTER_ALL;
  const employeeId = searchParams.get('employeeId') ?? FILTER_ALL;
  const groupByParam = searchParams.get('groupBy');
  const groupBy: ActivityLogGroupBy | undefined =
    groupByParam === 'client' || groupByParam === 'project' ? groupByParam : undefined;

  const { data: clients } = useClients();
  const { data: projects } = useProjects();
  const { data: employees } = useEmployees();

  const { data: report, isLoading } = useActivityLog({
    periodQs: mockupMode ? null : periodQs,
    clientId: clientId || undefined,
    projectId: projectId || undefined,
    employeeId: employeeId || undefined,
    groupBy,
  });

  // Filter cascade matches GroupedReportPage: switching the client clears
  // the project; if the chosen project belongs to a different client, drop
  // it.
  const onPickClient = (val: string) => {
    const next = new URLSearchParams(searchParams);
    if (val === FILTER_ALL) {
      next.delete('clientId');
      next.delete('projectId');
    } else {
      next.set('clientId', val);
      if (projectId) {
        const proj = projects.find((p) => p.id === projectId);
        if (proj && proj.clientId !== val) next.delete('projectId');
      }
    }
    setSearchParams(next);
  };

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  const onPickGroupBy = (choice: GroupByChoice) => {
    updateParam('groupBy', choice.value ?? null);
  };

  const filteredProjects = useMemo(
    () => (clientId ? projects.filter((p) => p.clientId === clientId) : projects),
    [projects, clientId],
  );

  const rows = report?.rows ?? [];
  const summary = report?.summary;
  const activeGroupBy = report?.groupBy ?? groupBy ?? null;

  const columns = useMemo(() => {
    const cols: Array<{
      key: ColumnKey;
      header: string;
      align?: 'left' | 'right';
      render: (row: ActivityLogRow) => React.ReactNode;
    }> = [];

    if (activeGroupBy) {
      cols.push({
        key: 'group',
        header: activeGroupBy === 'client' ? 'Client' : 'Project',
        render: (row) => row.groupLabel ?? '—',
      });
    }
    cols.push({
      key: 'titleApp',
      header: 'Application & Window Title',
      render: (row) => (
        <div className={styles.titleCell}>
          <div className={styles.titlePrimary}>
            {row.app}
            {row.title ? <span className={styles.titleSep}>{' — '}</span> : null}
            {row.title}
          </div>
          <div className={styles.titleSecondary}>
            {row.eventCount} screenshot{row.eventCount === 1 ? '' : 's'} · last seen{' '}
            {formatDayShort(row.lastOccurredAt)}
          </div>
        </div>
      ),
    });
    cols.push({
      key: 'sessions',
      header: 'Sessions',
      align: 'right',
      render: (row) => <span className={styles.mono}>{row.sessions}</span>,
    });
    cols.push({
      key: 'totalHours',
      header: 'Total Time',
      align: 'right',
      render: (row) => <span className={styles.mono}>{row.totalHours.toFixed(1)}h</span>,
    });
    cols.push({
      key: 'dateRange',
      header: 'Date Range',
      align: 'right',
      render: (row) => (
        <span className={styles.mono}>
          {formatDayShort(row.firstOccurredAt)} – {formatDayShort(row.lastOccurredAt)}
        </span>
      ),
    });
    return cols;
  }, [activeGroupBy]);

  const footer =
    summary && rows.length > 0 ? (
      <tr>
        {activeGroupBy ? <td /> : null}
        <td>
          Grand Total{report && report.totalGroups > rows.length ? ` (top ${rows.length})` : ''}
        </td>
        <td className={styles.mono}>{summary.totalSessions}</td>
        <td className={styles.mono}>{summary.totalHours.toFixed(1)}h</td>
        <td />
      </tr>
    ) : null;

  // Row keys must include the group key when grouped — otherwise React
  // collapses the split rows for the same (app, title) under two
  // projects to one DOM row, and you only see the first.
  const getRowKey = (row: ActivityLogRow) =>
    row.groupKey === null ? `${row.app}${row.title}` : `${row.app}${row.title}${row.groupKey}`;

  // Drill-down: each row links to /reports/activity-log/events with the
  // row identity (`app` + `title`) plus the active list filters and, if
  // grouped, `groupBy` + the row's `groupKey` so the detail page narrows
  // to the same slice of events the row was rolled up from.
  const buildRowHref = (row: ActivityLogRow): string => {
    const next = new URLSearchParams();
    next.set('app', row.app);
    next.set('title', row.title);
    if (clientId) next.set('clientId', clientId);
    if (projectId) next.set('projectId', projectId);
    if (employeeId) next.set('employeeId', employeeId);
    if (groupBy && row.groupKey) {
      next.set('groupBy', groupBy);
      next.set('groupKey', row.groupKey);
    }
    return `/reports/activity-log/events?${next.toString()}`;
  };

  return (
    <>
      <PageHeader
        title="Activity Log"
        subtitle="What your team is doing, grouped by application + window title — for activity review."
      />

      <div className={styles.reportControls}>
        <div className={styles.chipRow} data-testid="activity-log-groupby">
          <span className={styles.controlsLabel}>Group by:</span>
          {GROUP_BY_CHOICES.map((choice) => (
            <Button
              key={choice.id}
              variant="chip"
              size="sm"
              onClick={() => onPickGroupBy(choice)}
              aria-pressed={(activeGroupBy ?? null) === (choice.value ?? null)}
              data-testid={`activity-log-groupby-${choice.id}`}
            >
              {choice.label}
            </Button>
          ))}
        </div>
        <div className={styles.controlsRow}>
          <Select
            value={clientId || FILTER_ALL}
            options={[
              { value: FILTER_ALL, label: 'All clients' },
              ...clients.map((c) => ({ value: c.id, label: c.name })),
            ]}
            onChange={onPickClient}
            label="Client"
            size="sm"
          />
          <Select
            value={projectId || FILTER_ALL}
            options={[
              { value: FILTER_ALL, label: 'All projects' },
              ...filteredProjects.map((p) => ({ value: p.id, label: p.name })),
            ]}
            onChange={(v) => updateParam('projectId', v || null)}
            label="Project"
            size="sm"
          />
          <Select
            value={employeeId || FILTER_ALL}
            options={[
              { value: FILTER_ALL, label: 'All employees' },
              ...employees.map((e) => ({ value: e.id, label: e.name })),
            ]}
            onChange={(v) => updateParam('employeeId', v || null)}
            label="Employee"
            size="sm"
          />
        </div>
      </div>

      <DataTable<ActivityLogRow, ColumnKey>
        caption="Activity grouped by application and window title"
        columns={columns}
        rows={rows}
        getRowKey={getRowKey}
        rowHref={buildRowHref}
        emptyState={isLoading ? 'Loading…' : 'No activity in this window.'}
        footer={footer}
      />
    </>
  );
}
